import React, {useContext, useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {goVehisService} from "../../utils/axios";
import {CustomerTypeContext, isCustomerCompany} from "../../context/customer-type-context";
import {SearchOptionsContext} from "../../context/search-options-context";
import ConvertUtils from "../../utils/ConvertUtils";
import URLSearchParamsConverter from "../../utils/URLSearchParamsConverter";
import VehicleOfferListItem from "../../components/VehicleOfferListItem/VehicleOfferListItem";

const MAX_VISIBLE_OFFERS = 4;
const INSTALLMENT_TOLERANCE = 0.25;

const shuffle = (items) => {
    const shuffled = [...items];
    for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
};

function RecommendedOffers({offer}) {

    const customerContext = useContext(CustomerTypeContext);
    const currentCustomerType = customerContext[0];
    const searchOptions = useContext(SearchOptionsContext);
    const [offers, setOffers] = useState([]);
    const [searchString, setSearchString] = useState("");

    // The /vehicles filter uses its own body-type vocabulary (e.g. the vehicle
    // field says "SUV" while the filter expects "SUV / Crossover") — map the
    // offer's bodyType onto the closest filter option.
    const mapBodyTypeToFilterOption = () => {
        if (!offer.bodyType) return null;
        const optionsSource = offer.vehicleType === "DOSTAWCZE"
            ? (searchOptions.deliveryCars || searchOptions)
            : (searchOptions.passengerCars || searchOptions);
        const options = optionsSource.bodyTypes || [];
        const needle = offer.bodyType.toLowerCase();
        const match = options.find(option =>
            option.toLowerCase().includes(needle) || needle.includes(option.toLowerCase()));
        return match || offer.bodyType;
    };

    useEffect(() => {
        // ── Similar installment: ±25% around the current offer's rata ──
        const installment = isCustomerCompany(customerContext)
            ? offer.installmentNet
            : offer.installmentGross;
        const installmentKeyPrefix = isCustomerCompany(customerContext)
            ? "installmentNet"
            : "installmentGross";

        const buildFilterParams = (useLastFilters) => {
            const lastFilters = sessionStorage.getItem("lastOfferListFilters");
            const params = (useLastFilters && lastFilters)
                ? new URLSearchParams(lastFilters)
                : new URLSearchParams();

            // Rule 2 fallback: no previous filter → match by body type
            if ([...params.keys()].length === 0) {
                const bodyTypeOption = mapBodyTypeToFilterOption();
                if (bodyTypeOption) {
                    params.set("bodyTypes", bodyTypeOption);
                }
            }

            if (installment) {
                params.set(installmentKeyPrefix + "From", Math.floor(installment * (1 - INSTALLMENT_TOLERANCE)).toString());
                params.set(installmentKeyPrefix + "To", Math.ceil(installment * (1 + INSTALLMENT_TOLERANCE)).toString());
            }

            // Rule 3: for a new car recommend only new cars
            if (offer.condition === "NEW") {
                params.set("newUsed", "new");
            }

            if (offer.vehicleType) {
                params.set("vehicleTypes", offer.vehicleType);
            }

            return params;
        };

        const fetchOffers = (filterParams) => {
            const queryParams = new URLSearchParams(filterParams);
            queryParams.set("sortingType", "INSTALLMENT_ASC");
            queryParams.set("start", "0");
            queryParams.set("limit", "12");
            queryParams.set("customerType", currentCustomerType);
            URLSearchParamsConverter.applyPakietKorzysciToApiParams(queryParams);

            return goVehisService.get("/vehicles?" + queryParams.toString()).then(response => {
                const data = Array.isArray(response.data) ? response.data : [];
                return data.filter(item =>
                    !(item.vehicleIds || []).some(id => id.toString() === offer.id.toString()));
            });
        };

        // Rule 1: last used filters + similar rata; rule 2: body type + similar rata
        const primaryParams = buildFilterParams(true);
        const fallbackParams = buildFilterParams(false);

        fetchOffers(primaryParams).then(results => {
            if (results.length > 0) {
                setOffers(shuffle(results).slice(0, MAX_VISIBLE_OFFERS));
                setSearchString(primaryParams.toString());
                return null;
            }
            return fetchOffers(fallbackParams).then(fallbackResults => {
                setOffers(shuffle(fallbackResults).slice(0, MAX_VISIBLE_OFFERS));
                setSearchString(fallbackParams.toString());
            });
        }).catch(() => setOffers([]));
    }, [offer.id, currentCustomerType]);

    if (offers.length === 0) {
        return null;
    }

    return (
        <section className="recommended-offers">
            <h2 className="recommended-offers__title">Polecamy podobne oferty</h2>
            <div className="container recommended-offers__container">
                <div id="offers-list" className="grid-view">
                    {offers.map(item => (
                        <VehicleOfferListItem
                            isSliderOffer={false}
                            showRibbons={true}
                            key={item.uuid || item.vehicleIds[0]}
                            url={"/" + ConvertUtils.stringToUrl(item.brandName.replace("/", "") + "/" + item.versionName + "/" + item.vehicleIds[0])}
                            offer={item}
                        />
                    ))}
                </div>
            </div>
            <Link
                to={{pathname: "/lista-ofert", search: searchString}}
                className="recommended-offers__all-btn"
            >
                Zobacz wszystkie <i className="fa fa-chevron-right"/>
            </Link>
        </section>
    );
}

export default RecommendedOffers;
